// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Line } from "react-chartjs-2";
import { devicesApi } from "../../../Api/devices.api";
import { useDevice } from "../../../Api/newSockets.api";
import dataApi from "../../../Api/data.api";
import "../../../styles/mes.scss";

interface HostDeviceMetricsProps {
  device: any;
  deviceId: number;
  metrics: any;
  isOnline: boolean;
}

// Normalize metric keys from different sources
const normalizeMetric = (m: any) => {
  if (!m) return null;
  return {
    ...m,
    cpu: m.cpu ?? m.cpu_usage_percent,
    ram: m.ram ?? m.ram_usage_percent,
    ram_total: m.ram_total ?? (m.ram_usage_percent != null ? 100 : m.ram_total_mb),
    gpu: m.gpu ?? m.emc_usage_percent ?? m.gpu_usage_percent,
    disk: m.disk ?? m.disk_usage_percent,
    disk_total: m.disk_total ?? m.disk_total_gb,
    disk_used: m.disk_used ?? m.disk_used_gb,
    cpu_temp: m.cpu_temp ?? m.cpu_temp_c,
    gpu_temp: m.gpu_temp ?? m.temperature_c,
  };
};

const HostDeviceMetrics: React.FC<HostDeviceMetricsProps> = ({
  device,
  deviceId,
  metrics,
  isOnline,
}) => {
  // Metrics state - default to 1h like PLC/USB
  const [metricsTimeFilter, setMetricsTimeFilter] = useState<'live' | '1h' | '6h' | '24h' | 'custom'>('1h');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedCustomFrom, setAppliedCustomFrom] = useState('');
  const [appliedCustomTo, setAppliedCustomTo] = useState('');
  const [historicalMetrics, setHistoricalMetrics] = useState<{ createdAt: string; metrics: any }[]>([]);
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [copiedField, setCopiedField] = useState<'ip' | 'token' | null>(null);

  // Settings modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | "">(
    device?.settings?.defaultDataset ?? device?.settings?.defaultDataset ?? ""
  );
  const [newDatasetName, setNewDatasetName] = useState("");
  const [creatingDataset, setCreatingDataset] = useState(false);

  // Hooks for settings
  const { updateDeviceSettings } = useDevice();

  // Datasets are fetched over REST (request/response) rather than via the socket
  // 'datasets' event, which is race-prone on a hard reload: the emit and the event
  // listener attach in separate effects, so a fast response can arrive before the
  // listener exists and is silently dropped, leaving this dropdown empty. A REST GET
  // on mount has no such race and is retryable.
  const [datasets, setDatasets] = useState<any[]>([]);

  const fetchDatasets = async () => {
    try {
      const res = await dataApi.getDatasets(0, 200, { type: 'Metrics' });
      setDatasets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }
  };

  // Fetch datasets once on mount via REST (no socket-event timing race)
  useEffect(() => {
    fetchDatasets();
  }, []);

  // Dataset options
  const datasetOptions = useMemo(() => {
    return datasets?.map((d: any) => ({ value: d.id, label: d.name })) || [];
  }, [datasets]);

  // Update selected dataset when device settings change
  useEffect(() => {
    const datasetId = device?.settings?.defaultDataset ?? device?.settings?.defaultDataset;
    if (datasetId != null) {
      setSelectedDatasetId(Number(datasetId));
    }
  }, [device]);

  // Handlers
  const handleDatasetChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? Number(e.target.value) : "";
    setSelectedDatasetId(value);
    if (value) {
      await updateDeviceSettings(deviceId, { ...device.settings, defaultDataset: value });
    }
  };

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) return;
    setCreatingDataset(true);
    try {
      const newDataset = await dataApi.createDataset(newDatasetName.trim());
      if (newDataset?.id) {
        setSelectedDatasetId(newDataset.id);
        await updateDeviceSettings(deviceId, { ...device.settings, defaultDataset: newDataset.id });
      }
      setNewDatasetName("");
      await fetchDatasets();
    } catch (err) {
      console.error("Failed to create dataset:", err);
    } finally {
      setCreatingDataset(false);
    }
  };

  const copyToClipboard = (text: string, field: 'ip' | 'token') => {
    if (!text || text === '—') return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Consolidated HOST metrics state to reduce re-renders
  const [hostChartData, setHostChartData] = useState({
    cpu: new Array(12).fill(0),
    gpu: new Array(12).fill(0),
    ram: new Array(12).fill(0),
    disk: new Array(12).fill(0),
    cpuTemp: new Array(12).fill(0),
    gpuTemp: new Array(12).fill(0),
  });

  // Store last known metrics to prevent flashing
  const lastKnownMetricsRef = useRef<any>(null);

  // Latest metrics for current device only
  const deviceMetrics = useMemo(() => {
    const arr = metrics ? (Array.isArray(metrics) ? metrics : [metrics]) : [];
    const forThis = arr.filter((m: any) => m && Number(m.deviceId) === deviceId);
    const raw = forThis.length > 0 ? forThis[forThis.length - 1] : null;
    const normalized = normalizeMetric(raw);
    if (normalized) {
      lastKnownMetricsRef.current = normalized;
      return normalized;
    }
    // Return last known value if current update doesn't have this device's data
    return lastKnownMetricsRef.current;
  }, [metrics, deviceId]);

  // Process incoming real-time metrics (single state update)
  useEffect(() => {
    const arr = metrics ? (Array.isArray(metrics) ? metrics : [metrics]) : [];
    const forThis = arr.filter((m: any) => m && Number(m.deviceId) === deviceId);
    const raw = forThis.length > 0 ? forThis[forThis.length - 1] : null;
    const latest = normalizeMetric(raw);
    if (!latest) return;

    setHostChartData((prev) => {
      const addToArray = (arr: number[], value: number) =>
        arr.length >= 12 ? [...arr.slice(1), value] : [...arr, value];

      return {
        cpu: addToArray(prev.cpu, latest.cpu ?? 0),
        gpu: addToArray(prev.gpu, latest.gpu ?? 0),
        ram: addToArray(prev.ram, latest.ram ?? 0),
        disk: addToArray(prev.disk, latest.disk ?? 0),
        cpuTemp: addToArray(prev.cpuTemp, latest.cpu_temp ?? 0),
        gpuTemp: addToArray(prev.gpuTemp, latest.gpu_temp ?? 0),
      };
    });
  }, [metrics, deviceId]);

  // Fetch historical metrics (using REST API)
  useEffect(() => {
    if (!device) return;
    if (metricsTimeFilter === 'live') {
      setHistoricalMetrics([]);
      return;
    }
    const now = new Date();
    let from: Date | undefined;
    let to: Date | undefined = now;
    if (metricsTimeFilter === '1h') from = new Date(now.getTime() - 60 * 60 * 1000);
    else if (metricsTimeFilter === '6h') from = new Date(now.getTime() - 6 * 60 * 60 * 1000);
    else if (metricsTimeFilter === '24h') from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    else if (metricsTimeFilter === 'custom' && appliedCustomFrom && appliedCustomTo) {
      from = new Date(appliedCustomFrom);
      to = new Date(appliedCustomTo);
    } else return;

    setLoadingHistorical(true);
    devicesApi.getDeviceMetrics(deviceId, { from, to })
      .then((data) => setHistoricalMetrics(data || []))
      .catch((err) => {
        console.error('Failed to load historical metrics:', err);
        setHistoricalMetrics([]);
      })
      .finally(() => setLoadingHistorical(false));
  }, [metricsTimeFilter, appliedCustomFrom, appliedCustomTo, deviceId, device]);

  const applyCustomRange = () => {
    if (customFrom && customTo) {
      setAppliedCustomFrom(customFrom);
      setAppliedCustomTo(customTo);
      setMetricsTimeFilter('custom');
    }
  };

  // Chart data calculations - limit to 120 points max for performance
  const MAX_CHART_POINTS = 120;
  const chartUsesHistorical = metricsTimeFilter !== 'live';

  // Downsample historical data if needed
  const downsampledMetrics = useMemo(() => {
    if (!chartUsesHistorical || historicalMetrics.length <= MAX_CHART_POINTS) {
      return historicalMetrics;
    }
    // Take every Nth point to get ~MAX_CHART_POINTS
    const step = Math.ceil(historicalMetrics.length / MAX_CHART_POINTS);
    return historicalMetrics.filter((_, i) => i % step === 0);
  }, [historicalMetrics, chartUsesHistorical]);

  const chartLabels = chartUsesHistorical
    ? downsampledMetrics.map((m) => {
        const d = new Date(m.createdAt);
        return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      })
    : hostChartData.cpu.map((_, i) => i + 1);

  const chartCpuData = chartUsesHistorical
    ? downsampledMetrics.map((m) => (normalizeMetric(m.metrics)?.cpu ?? 0) as number)
    : hostChartData.cpu;

  const chartGpuData = chartUsesHistorical
    ? downsampledMetrics.map((m) => (normalizeMetric(m.metrics)?.gpu ?? 0) as number)
    : hostChartData.gpu;

  const chartLabels2 = chartUsesHistorical
    ? downsampledMetrics.map((m) => {
        const d = new Date(m.createdAt);
        return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      })
    : hostChartData.ram.map((_, i) => i + 1);

  const chartRamData = chartUsesHistorical
    ? downsampledMetrics.map((m) => (normalizeMetric(m.metrics)?.ram ?? 0) as number)
    : hostChartData.ram;

  const chartDiskData = chartUsesHistorical
    ? downsampledMetrics.map((m) => (normalizeMetric(m.metrics)?.disk ?? 0) as number)
    : hostChartData.disk;

  const chartLabels3 = chartUsesHistorical
    ? downsampledMetrics.map((m) => {
        const d = new Date(m.createdAt);
        return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      })
    : hostChartData.cpuTemp.map((_, i) => i + 1);

  const chartCpuTempData = chartUsesHistorical
    ? downsampledMetrics.map((m) => (normalizeMetric(m.metrics)?.cpu_temp ?? 0) as number)
    : hostChartData.cpuTemp;

  const chartGpuTempData = chartUsesHistorical
    ? downsampledMetrics.map((m) => (normalizeMetric(m.metrics)?.gpu_temp ?? 0) as number)
    : hostChartData.gpuTemp;

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* Header with device info and time filter */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
        borderRadius: '12px',
        border: '1px solid #53bde430',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ fontSize: '1.5rem' }}>
            🖥️
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#fff',
            }}>
              {device.name}
            </div>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: 'rgba(83, 189, 228, 0.2)',
              border: '1px solid #53bde4',
              color: '#53bde4',
            }}>
              Host
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => setSettingsModalOpen(true)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid #53bde440',
              background: 'rgba(83, 189, 228, 0.15)',
              color: '#53bde4',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span>⚙️</span> Settings
          </button>
          <span style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '0.5rem' }}>Time range:</span>
          <select
            value={metricsTimeFilter}
            onChange={(e) => setMetricsTimeFilter(e.target.value as any)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid #53bde440',
              background: 'rgba(33, 40, 44, 0.95)',
              color: '#c5c5c5',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            <option value="live">Live</option>
            <option value="1h">Last 1 hour</option>
            <option value="6h">Last 6 hours</option>
            <option value="24h">Last 24 hours</option>
            <option value="custom">Custom range</option>
          </select>
          {metricsTimeFilter === "custom" && (
            <>
              <input
                type="datetime-local"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #53bde440',
                  background: 'rgba(33, 40, 44, 0.95)',
                  color: '#c5c5c5',
                  fontSize: '0.8rem',
                }}
              />
              <span style={{ color: '#6c757d' }}>–</span>
              <input
                type="datetime-local"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #53bde440',
                  background: 'rgba(33, 40, 44, 0.95)',
                  color: '#c5c5c5',
                  fontSize: '0.8rem',
                }}
              />
              <button
                type="button"
                onClick={applyCustomRange}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 255, 136, 0.5)',
                  background: 'rgba(0, 255, 136, 0.15)',
                  color: '#00ff88',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Apply
              </button>
            </>
          )}
          {loadingHistorical && (
            <span style={{ color: '#53bde4', fontSize: '0.85rem' }}>Loading...</span>
          )}
        </div>
      </div>

      {/* Device Info Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
      }}>
        {/* Status */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: `1px solid ${isOnline ? '#00ff8830' : '#ff336630'}`,
          borderRadius: '10px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.25rem',
          }}>
            Status
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            flex: 1,
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isOnline ? '#00ff88' : '#ff3366',
              boxShadow: isOnline ? '0 0 6px #00ff88' : '0 0 6px #ff3366',
            }} />
            <span style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: isOnline ? '#00ff88' : '#ff3366',
            }}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* IP Address */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: '1px solid #53bde420',
          borderRadius: '10px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.25rem',
          }}>
            IP Address
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            minWidth: 0,
          }}>
            <div style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#fff',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}>
              {device?.settings?.public_ip || '—'}
            </div>
            {device?.settings?.public_ip && (
              <button
                onClick={() => copyToClipboard(device?.settings?.public_ip, 'ip')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  color: copiedField === 'ip' ? '#00ff88' : '#6c757d',
                  transition: 'color 0.2s',
                }}
                title="Copy to clipboard"
              >
                {copiedField === 'ip' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Token */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          border: '1px solid #53bde420',
          borderRadius: '10px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: '0.65rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.25rem',
          }}>
            Device Token
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1,
            minWidth: 0,
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#fff',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}>
              {device?.token || '—'}
            </div>
            {device?.token && (
              <button
                onClick={() => copyToClipboard(device?.token, 'token')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  color: copiedField === 'token' ? '#00ff88' : '#6c757d',
                  transition: 'color 0.2s',
                }}
                title="Copy to clipboard"
              >
                {copiedField === 'token' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        {/* CPU Usage */}
        <div style={{
          background: 'linear-gradient(135deg, #00ff8815 0%, #00ff8808 100%)',
          border: '1px solid #00ff8840',
          borderRadius: '10px',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#00ff88',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            fontWeight: 600,
          }}>
            CPU Usage
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'monospace',
          }}>
            {deviceMetrics?.cpu?.toFixed(1) ?? '—'}
            <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>%</span>
          </div>
        </div>

        {/* CPU Temp */}
        <div style={{
          background: 'linear-gradient(135deg, #ff9f4015 0%, #ff9f4008 100%)',
          border: '1px solid #ff9f4040',
          borderRadius: '10px',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#ff9f40',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            fontWeight: 600,
          }}>
            CPU Temp
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'monospace',
          }}>
            {deviceMetrics?.cpu_temp?.toFixed(1) ?? '—'}
            <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>°C</span>
          </div>
        </div>

        {/* GPU Usage */}
        <div style={{
          background: 'linear-gradient(135deg, #ff6b6b15 0%, #ff6b6b08 100%)',
          border: '1px solid #ff6b6b40',
          borderRadius: '10px',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#ff6b6b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            fontWeight: 600,
          }}>
            GPU Usage
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'monospace',
          }}>
            {deviceMetrics?.gpu?.toFixed(1) ?? '—'}
            <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>%</span>
          </div>
        </div>

        {/* GPU Temp */}
        <div style={{
          background: 'linear-gradient(135deg, #9966ff15 0%, #9966ff08 100%)',
          border: '1px solid #9966ff40',
          borderRadius: '10px',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#9966ff',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            fontWeight: 600,
          }}>
            GPU Temp
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'monospace',
          }}>
            {deviceMetrics?.gpu_temp?.toFixed(1) ?? '—'}
            <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>°C</span>
          </div>
        </div>

        {/* RAM */}
        <div style={{
          background: 'linear-gradient(135deg, #ffc10715 0%, #ffc10708 100%)',
          border: '1px solid #ffc10740',
          borderRadius: '10px',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#ffc107',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            fontWeight: 600,
          }}>
            RAM Usage
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'monospace',
          }}>
            {deviceMetrics?.ram?.toFixed(1) ?? '—'}
            <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>%</span>
          </div>
        </div>

        {/* Disk */}
        <div style={{
          background: 'linear-gradient(135deg, #4e9cff15 0%, #4e9cff08 100%)',
          border: '1px solid #4e9cff40',
          borderRadius: '10px',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.7rem',
            color: '#4e9cff',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            fontWeight: 600,
          }}>
            Disk Usage
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'monospace',
          }}>
            {deviceMetrics?.disk?.toFixed(1) ?? '—'}
            <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '4px' }}>
              {deviceMetrics?.disk_total != null ? 'GB' : '%'}
            </span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
      }}>
        {/* CPU & GPU Usage Chart */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          borderRadius: '12px',
          padding: '1.25rem',
          border: '1px solid #00ff8830',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              background: '#00ff88',
              borderRadius: '2px',
            }} />
            <div style={{
              fontSize: '0.9rem',
              color: '#00ff88',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}>
              CPU & GPU Usage
            </div>
          </div>
          <div style={{ height: '200px' }}>
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: "CPU %",
                    data: chartCpuData,
                    borderColor: "#00ff88",
                    backgroundColor: "rgba(0, 255, 136, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  },
                  {
                    label: "GPU %",
                    data: chartGpuData,
                    borderColor: "#ff6b6b",
                    backgroundColor: "rgba(255, 107, 107, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                  legend: {
                    position: "top",
                    labels: { color: "#c5c5c5", usePointStyle: true, boxWidth: 8, padding: 12 },
                  },
                  tooltip: {
                    backgroundColor: "rgba(33, 40, 44, 0.95)",
                    titleColor: "#c5c5c5",
                    bodyColor: "#c5c5c5",
                  },
                },
                scales: {
                  x: {
                    display: true,
                    grid: { color: "rgba(108, 117, 125, 0.1)" },
                    ticks: { color: "#6c757d", maxTicksLimit: 8, font: { size: 10 } },
                  },
                  y: {
                    min: 0,
                    max: 100,
                    grid: { color: "rgba(108, 117, 125, 0.1)" },
                    ticks: { color: "#6c757d", font: { size: 10 } },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Temperature Chart */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          borderRadius: '12px',
          padding: '1.25rem',
          border: '1px solid #ff9f4030',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              background: '#ff9f40',
              borderRadius: '2px',
            }} />
            <div style={{
              fontSize: '0.9rem',
              color: '#ff9f40',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}>
              Temperature
            </div>
          </div>
          <div style={{ height: '200px' }}>
            <Line
              data={{
                labels: chartLabels3,
                datasets: [
                  {
                    label: "CPU °C",
                    data: chartCpuTempData,
                    borderColor: "#ff9f40",
                    backgroundColor: "rgba(255, 159, 64, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  },
                  {
                    label: "GPU °C",
                    data: chartGpuTempData,
                    borderColor: "#9966ff",
                    backgroundColor: "rgba(153, 102, 255, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                  legend: {
                    position: "top",
                    labels: { color: "#c5c5c5", usePointStyle: true, boxWidth: 8, padding: 12 },
                  },
                  tooltip: {
                    backgroundColor: "rgba(33, 40, 44, 0.95)",
                    titleColor: "#c5c5c5",
                    bodyColor: "#c5c5c5",
                  },
                },
                scales: {
                  x: {
                    display: true,
                    grid: { color: "rgba(108, 117, 125, 0.1)" },
                    ticks: { color: "#6c757d", maxTicksLimit: 8, font: { size: 10 } },
                  },
                  y: {
                    min: 0,
                    grid: { color: "rgba(108, 117, 125, 0.1)" },
                    ticks: { color: "#6c757d", font: { size: 10 } },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* RAM & Disk Chart */}
        <div style={{
          background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
          borderRadius: '12px',
          padding: '1.25rem',
          border: '1px solid #ffc10730',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}>
            <div style={{
              width: '4px',
              height: '20px',
              background: '#ffc107',
              borderRadius: '2px',
            }} />
            <div style={{
              fontSize: '0.9rem',
              color: '#ffc107',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}>
              RAM & Disk
            </div>
          </div>
          <div style={{ height: '200px' }}>
            <Line
              data={{
                labels: chartLabels2,
                datasets: [
                  {
                    label: "RAM %",
                    data: chartRamData,
                    borderColor: "#ffc107",
                    backgroundColor: "rgba(255, 193, 7, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  },
                  {
                    label: "Disk %",
                    data: chartDiskData,
                    borderColor: "#4e9cff",
                    backgroundColor: "rgba(78, 156, 255, 0.1)",
                    fill: true,
                    tension: 0.3,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                  legend: {
                    position: "top",
                    labels: { color: "#c5c5c5", usePointStyle: true, boxWidth: 8, padding: 12 },
                  },
                  tooltip: {
                    backgroundColor: "rgba(33, 40, 44, 0.95)",
                    titleColor: "#c5c5c5",
                    bodyColor: "#c5c5c5",
                  },
                },
                scales: {
                  x: {
                    display: true,
                    grid: { color: "rgba(108, 117, 125, 0.1)" },
                    ticks: { color: "#6c757d", maxTicksLimit: 8, font: { size: 10 } },
                  },
                  y: {
                    min: 0,
                    max: 100,
                    grid: { color: "rgba(108, 117, 125, 0.1)" },
                    ticks: { color: "#6c757d", font: { size: 10 } },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {settingsModalOpen && (
        <div className="mes-modal-overlay" onClick={() => setSettingsModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">⚙️</div>
                <div className="modal-title-text">
                  <h3>HOST SETTINGS</h3>
                  <p className="modal-subtitle">Configure {device.name}</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setSettingsModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Default Dataset</label>
                <select
                  value={selectedDatasetId}
                  onChange={handleDatasetChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #53bde440',
                    background: 'rgba(33, 40, 44, 0.95)',
                    color: '#c5c5c5',
                    fontSize: '0.9rem',
                  }}
                >
                  <option value="">Select dataset...</option>
                  {datasetOptions.map((opt: any) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label>Create New Dataset</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newDatasetName}
                    onChange={(e) => setNewDatasetName(e.target.value)}
                    placeholder="Enter dataset name..."
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #53bde440',
                      background: 'rgba(33, 40, 44, 0.95)',
                      color: '#c5c5c5',
                      fontSize: '0.9rem',
                    }}
                  />
                  <button
                    onClick={handleCreateDataset}
                    disabled={creatingDataset || !newDatasetName.trim()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      border: '1px solid #53bde4',
                      background: creatingDataset ? 'rgba(83, 189, 228, 0.3)' : 'rgba(83, 189, 228, 0.2)',
                      color: '#53bde4',
                      fontSize: '0.9rem',
                      cursor: creatingDataset || !newDatasetName.trim() ? 'not-allowed' : 'pointer',
                      opacity: creatingDataset || !newDatasetName.trim() ? 0.5 : 1,
                    }}
                  >
                    {creatingDataset ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setSettingsModalOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostDeviceMetrics;
